import { Modal, Checkbox, Spin, message, Input, InputNumber } from 'antd';
import { Package } from '../../models/Package';
import { useEffect, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { assignPackagesToReseller, fetchAssignedPackages, fetchPackages } from '../../redux/slices/packageSlice';
interface AssignPackagesModalProps {
  visible: boolean;
  resellerId: number;
  onClose: () => void;
}

export function AssignPackagesModal({ visible, resellerId, onClose }: AssignPackagesModalProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { packages, loading } = useSelector((state: RootState) => state.packages);

  interface SelectedPackage {
    prepaidPackageTemplateId: string;
    adjustedValue: number;
  }

  const [selectedPackages, setSelectedPackages] = useState<SelectedPackage[]>([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchPackages());
  
        const assignedResult = await dispatch(fetchAssignedPackages(resellerId)).unwrap();
  
        // Create selected packages objects with default adjusted value of 1
        const selectedPkgs = assignedResult.map(pkg => ({
          prepaidPackageTemplateId: pkg.prepaidPackageTemplateId,
          adjustedValue: pkg.adjustedValue
        }));
        setSelectedPackages(selectedPkgs);
      } catch (error) {
        console.error('Failed to fetch assigned packages', error);
      }
    };
  
    if (visible) {
      fetchData();
      setSearch('');
    }
  }, [dispatch, visible, resellerId]);

  const handleAssign = async () => {
    if (!selectedPackages.length || !resellerId) return;
    
    setSaving(true);
    try {
      // Convert selected packages to the format expected by the API
      const packageAssignments = selectedPackages.map(pkg => ({
        packageId: pkg.prepaidPackageTemplateId,
        adjustedValue: pkg.adjustedValue
      }));

      const resultAction = await dispatch(assignPackagesToReseller({ 
        resellerId, 
        packages: packageAssignments
      }));
  
      if (assignPackagesToReseller.fulfilled.match(resultAction)) {
        message.success('Packages assigned successfully');
        onClose();
      } else {
        message.error(resultAction.payload as string || 'Failed to assign packages');
      }
    } finally {
      setSaving(false);
    }
  };


  const [sortedPackages, setSortedPackages] = useState<Package[]>([]);

  useEffect(() => {
    const filtered = packages
      .filter(pkg => pkg.prepaidPackageTemplateName.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        const aIsSelected = selectedPackages.some(sp => sp.prepaidPackageTemplateId === a.prepaidPackageTemplateId);
        const bIsSelected = selectedPackages.some(sp => sp.prepaidPackageTemplateId === b.prepaidPackageTemplateId);
        
        if (aIsSelected && !bIsSelected) return -1; // a comes first
        if (!aIsSelected && bIsSelected) return 1;  // b comes first
        return 0; // maintain original order
      });
    
    setSortedPackages(filtered);
  }, [packages, selectedPackages, search]);

  const handleCheckboxChange = (checked: boolean, id: string) => {
    setSelectedPackages((prev) => {
      if (checked) {
        // Add the package with default adjusted value of 1
        return [...prev, { prepaidPackageTemplateId: id, adjustedValue: 1 }];
      } else {
        // Remove the package
        return prev.filter(item => item.prepaidPackageTemplateId !== id);
      }
    });
    // Note: No need to manually sort here as the useEffect will handle it
  };

  const handleAdjustedValueChange = (value: number | null, id: string) => {
    setSelectedPackages(prev => {
      return prev.map(pkg => {
        if (pkg.prepaidPackageTemplateId === id) {
          return { ...pkg, adjustedValue: value || 1 }; // Default to 1 if null
        }
        return pkg;
      });
    });
  };

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const pkg = sortedPackages[index];
    const selectedPkg = selectedPackages.find(item => item.prepaidPackageTemplateId === pkg.prepaidPackageTemplateId);
    const isChecked = !!selectedPkg;

    return (
      <div style={{ ...style, display: 'flex', alignItems: 'center', padding: '0 8px' }}>
        <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
          <Checkbox
            checked={isChecked}
            onChange={(e) => handleCheckboxChange(e.target.checked, pkg.prepaidPackageTemplateId)}
            style={{ flexShrink: 0 }}
          />
          <div style={{ marginLeft: 8, flexGrow: 1 }}>
            <strong>{pkg.prepaidPackageTemplateName}</strong> — {pkg.periodDays}d, {(pkg.dataByte / (1024 * 1024)).toFixed(0)}MB, {pkg.cost}€
          </div>
          {isChecked && (
            <InputNumber
              min={0.01}
              step={0.01}
              defaultValue={1}
              value={selectedPkg?.adjustedValue || 1}
              onChange={(value) => handleAdjustedValueChange(value, pkg.prepaidPackageTemplateId)}
              style={{ width: 80, marginLeft: 8 }}
              disabled={!isChecked}
              precision={0} // Set precision to 0 to avoid decimal formatting
              formatter={(value) => `${value}`} // Simple formatter that doesn't add decimals
              parser={(value) => value ? Number(value) : 1} // Simple parser
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      title="Assign Packages"
      onCancel={onClose}
      onOk={handleAssign}
      okButtonProps={{ loading: saving, disabled: selectedPackages.length === 0 }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnClose
      width={700}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Input
            placeholder="Search packages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginBottom: 16 }}
          />

          <div style={{ height: 400, overflow: 'auto', border: '1px solid #eee', borderRadius: 4 }}>
            <List
              height={400}
              itemCount={sortedPackages.length}
              itemSize={50}
              width="100%"
            >
              {Row}
            </List>
          </div>
        </>
      )}
    </Modal>
  );
}
